import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation, Language, getQuestions } from "@/utils/translations";

interface DecisionInputProps {
  onSubmit: (decision: string, questionCount: number) => void;
  isLoading: boolean;
}

export const DecisionInput = ({ onSubmit, isLoading }: DecisionInputProps) => {
  const [decision, setDecision] = useState("");
  const [questionCount, setQuestionCount] = useState("5");
  const [currentLang, setCurrentLang] = useState<Language>("en");

  useEffect(() => {
    const savedLang = localStorage.getItem("preferredLanguage") as Language;
    if (savedLang) {
      setCurrentLang(savedLang);
    }
  }, []);

  const t = useTranslation(currentLang);
  const questions = getQuestions(currentLang);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (decision.trim()) {
      onSubmit(decision, parseInt(questionCount));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center min-h-screen p-4"
    >
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-4">{t("makeDecision")}</h1>
        <p className="text-gray-600 dark:text-gray-300">{t("enterQuestion")}</p>
      </div>

      <form onSubmit={handleSubmit} className="w-full max-w-md">
        <input
          type="text"
          value={decision}
          onChange={(e) => setDecision(e.target.value)}
          placeholder={t("shouldI")}
          className="decision-input mb-4"
          disabled={isLoading}
        />
        <div className="mb-4">
          <Select
            value={questionCount}
            onValueChange={setQuestionCount}
            disabled={isLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("numberOfQuestions")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">{questions["3"]}</SelectItem>
              <SelectItem value="5">{questions["5"]}</SelectItem>
              <SelectItem value="7">{questions["7"]}</SelectItem>
              <SelectItem value="10">{questions["10"]}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          type="submit"
          className="w-full"
          disabled={!decision.trim() || isLoading}
        >
          {isLoading ? t("generatingQuestions") : t("start")}
        </Button>
      </form>
    </motion.div>
  );
};
